import { ApiError, type Page, type Project, type Workspace } from "@baton/api";
import type { Runtime } from "@baton/core";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { useEffect } from "react";
import { meQuery, projectsQuery, workspaceQuery, workspacesQuery, type MeView } from "../../app/queries.ts";

/**
 * FR-015-003, FR-015-013, `contracts/screens.md` § Shell data: on mount the shell loads
 * `GET /me`, `GET /workspaces`, `GET /workspaces/{ws}` and `GET /workspaces/{ws}/projects`
 * in parallel. A 404 on the workspace is the `no-access` state; a 401 is parked behind the
 * re-authentication dialog by the signed-in client. The unread count is read by the badge
 * (`topbar/InboxBadge.tsx`) and the `GET /workspaces/{ws}/events` stream is opened beside this
 * loader by `realtime/useWorkspaceStream.ts`; the search family is registered with the query
 * client here so its defaults apply once the workspace is known.
 */
export interface ShellData {
  readonly me: UseQueryResult<MeView>;
  readonly workspaces: UseQueryResult<Page<Workspace>>;
  readonly workspace: UseQueryResult<Workspace>;
  readonly projects: UseQueryResult<Page<Project>>;
  /** The container answered 403 or 404: the person cannot see this workspace. */
  readonly noAccess: boolean;
}

export function useShellData(runtime: Runtime, ws: string): ShellData {
  useEffect(() => {
    runtime.queryClient.registerWorkspace(ws);
  }, [runtime, ws]);
  const [me, workspaces, workspace, projects] = useQueries({
    queries: [meQuery(runtime), workspacesQuery(runtime), workspaceQuery(runtime, ws), projectsQuery(runtime, ws)],
  });
  const status = workspace.error instanceof ApiError ? workspace.error.status : null;
  return { me, workspaces, workspace, projects, noAccess: status === 404 || status === 403 };
}
